import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SeoTags } from "../../components/Head/SeoTags";
import {
  Spaces,
  AITagging,
  Encryption,
  MultiCollection,
  Archiving,
  Cleanup,
  Rediscover,
} from "./Callout";
import { SearchMock } from "../../components/Mockups/SearchMock";
import { ExtensionMock } from "../../components/Mockups/ExtensionMock";
import { ExploreMore } from "../../components/Home/ExploreMore";

const Features = () => {
  const { t } = useTranslation();

  return (
    <>
      <SeoTags
        title={t("features.seo.title")}
        description={t("features.seo.description")}
        keywords={t("features.seo.keywords")}
      />

      {/* WebPage Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: t("features.seo.title"),
            description: t("features.seo.description"),
            url: "https://onbookmarks.com/features/",
            isPartOf: {
              "@type": "WebSite",
              name: "onBookmarks",
              url: "https://onbookmarks.com"
            },
            mainEntity: {
              "@type": "SoftwareApplication",
              name: "onBookmarks",
              featureList: [
                t("features.hero.highlights.instantSearch"),
                t("features.hero.highlights.advancedAI"),
                t("features.hero.highlights.fullyPrivate"),
                t("features.globalSearch.title"),
                t("features.browserExtension.title")
              ]
            }
          }),
        }}
      />

      {/* ItemList Schema for Features */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "onBookmarks Features",
            description: t("features.seo.description"),
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: t("features.hero.highlights.instantSearch"),
                description: t("features.globalSearch.description")
              },
              {
                "@type": "ListItem",
                position: 2,
                name: t("features.hero.highlights.advancedAI"),
                description: "AI-powered bookmark organization and tagging"
              },
              {
                "@type": "ListItem",
                position: 3,
                name: t("features.hero.highlights.fullyPrivate"),
                description: "Client-side encryption and privacy-first design"
              },
              {
                "@type": "ListItem",
                position: 4,
                name: t("features.browserExtension.title"),
                description: t("features.browserExtension.description")
              }
            ]
          }),
        }}
      />

      {/* Breadcrumb Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://onbookmarks.com"
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Features",
                item: "https://onbookmarks.com/features/"
              }
            ]
          }),
        }}
      />

      <section className="hero py-5 rounded-bottom-4">
        <div className="container position-relative">
          <div className="row">
            <div className="col-lg-10 mx-auto">
              <div className="text-center mb-5">
                <h1 className="display-5 fw-bold mb-4">
                  {t("features.hero.title")}
                </h1>

                <p
                  className="lead text-muted mx-auto"
                  style={{ maxWidth: "700px" }}
                >
                  {t("features.hero.description")}
                </p>
              </div>

              {/* Quick feature highlights */}
              <div className="row g-3 justify-content-center">
                <div className="col-auto">
                  <span className="rounded-5 bg-white text-dark px-3 py-3 shadow-sm">
                    <i className="bi bi-lightning-charge text-warning me-2"></i>
                    {t("features.hero.highlights.instantSearch")}
                  </span>
                </div>
                <div className="col-auto">
                  <span className="rounded-5 bg-white text-dark px-3 py-3 shadow-sm">
                    <i className="bi bi-robot text-primary me-2"></i>
                    {t("features.hero.highlights.advancedAI")}
                  </span>
                </div>
                <div className="col-auto">
                  <span className="rounded-5 bg-white text-dark px-3 py-3 shadow-sm">
                    <i className="bi bi-shield-check text-success me-2"></i>
                    {t("features.hero.highlights.fullyPrivate")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <MultiCollection />

      <Encryption />

      <AITagging />

      <Spaces />

      <section id="global-search" className="container section-padding pb-0">
        <div className="row align-items-center">
          <div className="col-xl-6 col-lg-6 col-12 order-1 order-lg-1">
            <div className="d-flex flex-column gap-4 me-0 me-lg-4">
              <div className="d-flex flex-column gap-2">
                <h3 className="mb-0 fw-bold fs-2">
                  {t("features.globalSearch.title")}
                </h3>
                <p className="mb-0">{t("features.globalSearch.description")}</p>
                <div className="mt-3">
                  <div className="d-flex align-items-center gap-3 mb-2">
                    <i className="bi bi-search fs-3 text-danger"></i>
                    <h4 className="fs-4 fw-semibold mb-0">
                      {t("features.globalSearch.instant.title")}
                    </h4>
                  </div>
                  <p className="mb-0">
                    {t("features.globalSearch.instant.description")}
                  </p>
                </div>
                <div className="mt-3">
                  <div className="d-flex align-items-center gap-3 mb-2">
                    <i className="bi bi-funnel fs-3 text-danger"></i>
                    <h4 className="fs-4 mb-0">
                      {t("features.globalSearch.smart.title")}
                    </h4>
                  </div>
                  <div>
                    <p className="mb-0">
                      {t("features.globalSearch.smart.description")}
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-1">
                <Link
                  to="/features/global-search"
                  className="btn btn-none d-inline-flex p-0 text-danger align-items-center gap-2"
                >
                  {t("features.buttons.globalSearch")}
                  <i className="bi bi-arrow-right"></i>
                </Link>
              </div>
            </div>
          </div>
          <div className="col-xl-6 col-lg-6 col-12 order-2 order-lg-2">
            <div className="bg-danger bg-opacity-10 p-3 p-md-4 mt-4 mt-lg-0 rounded-4 d-flex justify-content-center">
              <SearchMock />
            </div>
          </div>
        </div>
      </section>

      <section
        id="browser-extension"
        className="container section-padding pb-0"
      >
        <div className="row align-items-center">
          <div className="col-xl-6 col-lg-6 col-12 order-2 order-lg-1">
            <div className="d-flex bg-info-subtle justify-content-center p-3 p-md-4 mt-4 mt-lg-0 rounded-4 me-0 me-lg-5">
              <ExtensionMock />
            </div>
          </div>
          <div className="col-xl-6 col-lg-6 col-12 order-1 order-lg-2">
            <div className="d-flex flex-column gap-4">
              <div className="d-flex flex-column gap-2">
                <h3 className="mb-0 fw-bold fs-2">
                  {t("features.browserExtension.title")}
                </h3>
                <p className="mb-0">
                  {t("features.browserExtension.description")}
                </p>
                <div className="mt-3">
                  <div className="d-flex align-items-center gap-3 mb-2">
                    <i className="bi bi-cursor-fill fs-2 text-info"></i>
                    <h4 className="fs-4 fw-semibold mb-0">
                      {t("features.browserExtension.quickSave.title")}
                    </h4>
                  </div>
                  <p className="mb-0">
                    {t("features.browserExtension.quickSave.description")}
                  </p>
                </div>
              </div>
              <div className="mt-1">
                <Link
                  to="/download/extensions"
                  className="btn btn-none p-0 text-info d-inline-flex align-items-center gap-2"
                >
                  {t("features.buttons.browserExtension")}
                  <i className="bi bi-arrow-right"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Library-longevity features — big callouts */}
      <section className="container section-padding pb-0 text-center">
        <div className="col-lg-8 mx-auto">
          <h2 className="fw-bold mb-3">{t("features.moreFeatures.title")}</h2>
          <p className="lead text-muted mb-0">
            {t("features.moreFeatures.description")}
          </p>
        </div>
      </section>

      <Archiving />

      <Cleanup />

      <Rediscover />

      <ExploreMore />

      <section className="bg-light my-5 py-5">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-8 mx-auto text-center">
              <h2 className="fw-bold mb-4">{t("features.cta.title")}</h2>
              <p className="lead mb-4">{t("features.cta.description")}</p>
              <Link
                to={`${process.env.REACT_APP_APP_URL}/register`}
                className="btn btn-primary px-5"
              >
                {t("features.cta.button")}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Features;
